import { Component, h } from 'preact'
import UserAvatar from '../User/UserAvatar'
import UserName from '../User/UserName'
import classNames from 'classnames'
import axios from 'axios'
import Phrase from '../../libs/translationsUtils/phrase'
import Icon from '../assets'
import { formatLeaderboardPosition, normalizeUserData } from '../leaderboard/utils'
import qs from 'qs'

export interface User {
  id: number
  name: string
  avatar: string
  points: number
  leaderboardPosition: number
  rank?: string
}

interface State {
  usersList: User[]
  isLoading: boolean
  currentUser?: User
}

interface Props {
  maxResults: number
  excludeUserRole: string[]
  tabTitle: string
  key: string
  emptyMessage: string
  isPreviewEmptyMessage?: boolean
  usersList?: User[]
  showLink?: boolean
  linkText?: string
  showLoggedInUserSection?: boolean
}

const minUsersRequiredOnLeaderboard = 2
const topLeaderboardUsers = 3

class LeaderboardUserList extends Component<Props, State> {
  private readonly isGuest: boolean = (typeof window !== 'undefined') ? !window.inSidedData.user.userid : true
  private isCustomizing: boolean = false
  constructor (props: Props) {
    super(props)
    this.state = {
      usersList: props.usersList ?? [],
      isLoading: false
    }
  }

  async componentDidMount () {
    const searchQuery = typeof window !== 'undefined' ? window.location.search : ''
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })
    this.isCustomizing = 'customize' in queryParameters
    this.setState({ isLoading: true })
    const { tabTitle } = this.props
    const fetchLoggedInUserSection: boolean | undefined = this.props.showLoggedInUserSection && !this.isGuest
    const [usersList, leaderboardUser] = await Promise.all([this.fetchLeaderboardWidget(), fetchLoggedInUserSection ? this.fetchLoggedInLeaderboardUser(tabTitle) : undefined])
    this.setState({ usersList: usersList, currentUser: leaderboardUser, isLoading: false })
  }

  async fetchLeaderboardWidget () {
    try {
      const res = await axios.get('/widget/pointsLeaderboard', {
        params: {
          period: this.props.tabTitle,
          pageSize: this.props.maxResults,
          excludeRoles: this.props.excludeUserRole.join(','),
          showLink: this.props.showLink,
          linkText: this.props.linkText
        }
      })
      if (res.data) {
        return res.data
      }
    } catch {
      this.setState({ isLoading: false })
      return []
    }
  }

  async fetchLoggedInLeaderboardUser (period: string) {
    try {
      const res = await axios.get('/leaderboard/fetchUser', {
        params: {
          period: period,
          widget: true,
          excludeRoles: this.props.excludeUserRole.join(',')
        }
      })
      if (Object.keys(res.data).length !== 0) {
        return res.data
      }
    } catch {
      this.setState({ isLoading: false })
      return undefined
    }
  }

  render ({ isPreviewEmptyMessage, emptyMessage, tabTitle, linkText, showLink, showLoggedInUserSection }: Props, { usersList, isLoading, currentUser }: State) {
    const displayLoggedInUserSection: boolean | undefined = showLoggedInUserSection && !this.isGuest
    return (
      <div className='leaderboard-tab'>
        {
          isLoading
            ? ''
            : <div>
              {usersList.length > minUsersRequiredOnLeaderboard && (!this.isCustomizing || (this.isCustomizing && !isPreviewEmptyMessage))
                ? (
                  <div className='leaderboard-widget-table-container'>
                    <section className='leaderboard-widget-table'>
                      <div className='leaderboard-widget-table-body'>
                        {displayLoggedInUserSection && <LeaderboardUserRow user={currentUser} customClass='leaderboard-row-current-user' isLoggedInUser isLoggedInUserSection />}
                        {usersList.map(user => <LeaderboardUserRow key={user.id} user={user} />)}
                      </div>
                    </section>
                  </div>)
                : <div
                    className='default-leaderboard-meta-text'
                    dangerouslySetInnerHTML={{ __html: emptyMessage }}
                  />}
              {showLink && <div className='leaderboard-link-text'>
                <a href={`/leaderboard?period=${tabTitle}`} className='btn--cta btn--show-more'> {linkText}
                  <Icon customClass='leaderboard-link-text-icon' icon='chevronRight' height={20} width={20} />
                </a>
              </div>}
            </div>
}
      </div>
    )
  }
}

interface LeaderboardUserRowProps {
  key?: number
  user?: User
  isLoggedInUser?: boolean
  isLoggedInUserSection?: boolean
  customClass?: string
}

const LeaderboardUserRow = ({ user, isLoggedInUser = false, isLoggedInUserSection = false, customClass }: LeaderboardUserRowProps) => {
  if (!user) {
    return null
  }
  const { id, name, avatar, points, leaderboardPosition, rank } = user
  const userData = normalizeUserData(id, name, avatar, rank, isLoggedInUser)
  const loggedInUserData = normalizeUserData(id, name, avatar, rank, isLoggedInUser, isLoggedInUserSection)
  const isMedalVisible = leaderboardPosition !== null && leaderboardPosition <= topLeaderboardUsers
  const isTopThree = leaderboardPosition <= topLeaderboardUsers
  const leaderboardPositionToDisplay = isLoggedInUserSection && formatLeaderboardPosition(leaderboardPosition, points)

  return (
    <div className={`leaderboard-row ${customClass ?? ''}`}>
      <div className='leaderboard-user-position'>
        {isMedalVisible &&
          <Icon icon='medal' customClass={`icon-medal mobile-medal-icon icon-medal-${leaderboardPosition} is-hidden-S is-hidden-L`} />}
        <span className={classNames('position-number text--meta', isTopThree ? 'position-number-top' : 'position-number-other')}>{leaderboardPosition}</span>
        {isLoggedInUserSection && <div className='logged-in-user-section-ranking-wrapper'>
          <div className='logged-in-user-section-ranking'>{leaderboardPositionToDisplay}</div>
          <div className='logged-in-user-section-ranking-label text--meta'><Phrase p='leaderboard.header.ranking' /></div>
        </div>}
      </div>
      <div className='leaderboard-user-details'>
        <div className='leaderboard-user-avatar'><UserAvatar user={userData} showRank style={{ display: 'block' }} /></div>
        <div className='leaderboard-username'>
          <div>
            <UserName user={loggedInUserData} extraClass='username' />
            <p className='leaderboard-user-points is-hidden-only-M'><Phrase m='Forum' p='{n} point| {n} points' count={{ n: points.toString() }} /></p>
          </div>
        </div>
      </div>
      <div className='leaderboard-user-points-M is-hidden-S is-hidden-L'> <Phrase m='Forum' p='{n} point| {n} points' count={{ n: points.toString() }} /></div>
      {isLoggedInUserSection && <div className='logged-in-user-section-points-wrapper'>
        <div className='logged-in-user-section-points'>{points}</div>
        <div className='logged-in-user-section-points-label text--meta'><Phrase p='leaderboard.header.points' /></div>
      </div>}
      <div className='leaderboard-user-medal is-hidden-only-M'>{isMedalVisible && <Icon icon='medal' customClass={`icon-medal mobile-medal-icon icon-medal-${leaderboardPosition}`} />}</div>
    </div>
  )
}

export default LeaderboardUserList
